import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Page } from "@/components/Shell";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtTime } from "@/lib/api";

const Section = ({ label, children }) => (
  <section className="grid md:grid-cols-12 gap-6 md:gap-12 py-12 border-b border-[#1c1c1c]">
    <p className="md:col-span-3 eyebrow pt-1">{label}</p>
    <div className="md:col-span-9">{children}</div>
  </section>
);

export default function SettingsPage() {
  const { user, reload, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [voices, setVoices] = useState([]);
  const [audit, setAudit] = useState([]);
  const [usage, setUsage] = useState([]);
  useEffect(() => {
    api.get("/settings").then((r) => setVoices(r.data.voices)).catch(() => {});
    api.get("/audit").then((r) => setAudit(r.data)).catch(() => {});
    api.get("/usage").then((r) => setUsage(r.data)).catch(() => {});
  }, []);
  const s = user?.settings || {};
  const patch = async (body) => { try { await api.patch("/settings", body); await reload(); toast("Saved."); } catch (e) { toast.error(errMsg(e)); } };
  const removeAccount = async () => {
    try { await api.delete("/account"); setUser(false); navigate("/"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Page eyebrow="Account" title="Settings" intro={user?.email}>
      <Section label="Profile">
        <div className="flex flex-col sm:flex-row gap-4 max-w-xl">
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name-input" placeholder="Your name"
            className="flex-1 bg-transparent border-b border-[#2A2A2A] focus:border-[#D8BFD8] outline-none py-3 text-ivory" />
          <button onClick={() => patch({ name })} data-testid="settings-save-name" className="px-6 py-3 rounded-full border border-[#2A2A2A] text-xs tracking-[0.2em] uppercase text-[#C0C0C0] hover:text-ivory transition-colors">Save</button>
        </div>
      </Section>
      <Section label="Voice">
        <div className="space-y-8 max-w-xl">
          <label className="flex items-center justify-between text-sm text-ivory">Speak replies aloud after voice input
            <Switch checked={s.spoken_replies !== false} onCheckedChange={(v) => patch({ spoken_replies: v })} data-testid="settings-spoken-switch" /></label>
          <div className="flex flex-wrap gap-2" data-testid="settings-voice-options">
            {voices.map((v) => (
              <button key={v} onClick={() => patch({ voice: v })} data-testid={`settings-voice-${v}`}
                className={`px-4 py-2 border text-xs tracking-[0.16em] uppercase transition-colors ${s.voice === v ? "border-[#D8BFD8] text-[#D8BFD8]" : "border-[#2A2A2A] text-[#8A8A8A] hover:text-ivory"}`}>{v}</button>
            ))}
          </div>
        </div>
      </Section>
      <Section label="Notifications">
        <div className="flex gap-2" data-testid="settings-notify-options">
          {["web", "whatsapp"].map((c) => (
            <button key={c} onClick={() => patch({ notify_channel: c })} data-testid={`settings-notify-${c}`}
              className={`px-4 py-2 border text-xs tracking-[0.16em] uppercase transition-colors ${s.notify_channel === c ? "border-[#D8BFD8] text-[#D8BFD8]" : "border-[#2A2A2A] text-[#8A8A8A] hover:text-ivory"}`}>{c}</button>
          ))}
        </div>
        <p className="mt-4 text-xs text-[#5f5f5f]">Proactive reminders are not built yet; this preference is stored for when they are.</p>
      </Section>
      <Section label="Usage">
        {usage.length === 0 ? <p className="text-sm text-[#5f5f5f]">No usage recorded yet.</p> : (
          <ul className="space-y-2 text-sm" data-testid="settings-usage">{usage.map((u) => <li key={u.kind} className="flex justify-between max-w-md text-[#C0C0C0]"><span>{u.kind}</span><span className="text-[#8A8A8A]">{Math.round(u.units)} · {u.count} {u.count === 1 ? "call" : "calls"}</span></li>)}</ul>
        )}
      </Section>
      <Section label="Audit trail">
        <ul className="space-y-2 font-mono text-[0.7rem] text-[#8A8A8A] max-h-72 overflow-y-auto scrollbar-thin" data-testid="settings-audit">
          {audit.map((a) => <li key={a.id}><span className="text-[#4a4a4a]">{fmtTime(a.created_at)}</span> {a.action}</li>)}
        </ul>
      </Section>
      <Section label="Session">
        <div className="flex flex-wrap gap-4">
          <button onClick={async () => { await logout(); navigate("/"); }} data-testid="settings-logout-button" className="px-6 py-3 rounded-full border border-[#2A2A2A] text-xs tracking-[0.2em] uppercase text-[#C0C0C0] hover:text-ivory transition-colors">Sign out</button>
          <AlertDialog>
            <AlertDialogTrigger asChild><button data-testid="settings-delete-account-button" className="px-6 py-3 rounded-full border border-[#e2a6a6]/30 text-xs tracking-[0.2em] uppercase text-[#e2a6a6] hover:border-[#e2a6a6]/70 transition-colors">Delete account</button></AlertDialogTrigger>
            <AlertDialogContent className="bg-[#121212] border-[#2A2A2A] rounded-none">
              <AlertDialogHeader><AlertDialogTitle className="font-display text-2xl text-ivory">Delete everything?</AlertDialogTitle>
                <AlertDialogDescription>Conversations, memories, tasks, approvals and linked channels will be permanently removed.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel data-testid="delete-account-cancel">Keep account</AlertDialogCancel>
                <AlertDialogAction onClick={removeAccount} data-testid="delete-account-confirm" className="bg-[#4A1C1C] text-ivory hover:bg-[#5a2222]">Delete permanently</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </Section>
    </Page>
  );
}
